import { q1DocFunctions } from './q1-core-functions.js';
import { capFirstChar, estimateReadingTime, getFirstParagraph } from './string-helpers-client.js';
import { enrichGuild } from './guild-helpers-client.js';
import { enrichProject } from './project-helpers-client.js';
import { enrichMember } from './member-helpers-client.js';

import {
  versionSubjectCollectionMap,
  versionStatusMap,
  versionApprovalStatusMap,
  versionApprovalFlowMap,
  versionResponseStatusMap,
} from '../models/version-client-enums.js';

const FIRST_PARAGRAPH_MIN_LENGTH = 200;

const _addQuotes = (text) => {
  return '<span class="quote left">“</span> ' + text + ' <span class="quote right">”</span>';
};


/**
 * Enriches the version with additional properties for display purposes.
 * 
 * @param {Object} version - Required. The version object to enrich.
 * @param {Object} refPathMap - Optional, but recommended. An object containing maps of related 
 *   objects by their ref path strings. They will be enriched in place as they are used.
 */
export const enrichVersion = (version, refPathMap) => {
  if (version.isEnriched) return version;

  if (!refPathMap) refPathMap = {};

  // First create some shortcuts
  const { subjectCollection, status, approvalStatus, approvalFlow } = version;

  const subjectCollectionSettings = versionSubjectCollectionMap[subjectCollection];
  const subjectCollection_icon = subjectCollectionSettings?.icon || 'help';
  const subjectCollection_iconLibrary = subjectCollectionSettings?.iconLibrary || 'material';
  const subjectCollection_label = subjectCollectionSettings?.label || 'Unknown';
  const parentCollection = subjectCollectionSettings?.parentCollection || null;
  const parentAvatarShape = subjectCollectionSettings?.parentAvatarShape || 'circle';

  const statusSettings = versionStatusMap[status];
  const status_icon = statusSettings?.icon || 'help';
  const status_iconLibrary = statusSettings?.iconLibrary || 'material';
  const status_label = statusSettings?.label || 'Unknown';
  const status_description = statusSettings?.description || '';
  
  const approvalStatusSettings = versionApprovalStatusMap[approvalStatus];
  const approvalStatus_icon = approvalStatusSettings?.icon || 'help';
  const approvalStatus_iconLibrary = approvalStatusSettings?.iconLibrary || 'material';
  const approvalStatus_label = approvalStatusSettings?.label || 'Unknown';
  const approvalStatus_description = approvalStatusSettings?.description || '';
  
  const approvalFlowSettings = versionApprovalFlowMap[approvalFlow];
  const approvalFlow_icon = approvalFlowSettings?.icon || 'help';
  const approvalFlow_iconLibrary = approvalFlowSettings?.iconLibrary || 'material';
  const approvalFlow_label = approvalFlowSettings?.label || 'Unknown';
  const approvalFlow_versionKey = `${approvalFlow}:v${version.approvalFlowVersion}`;

  // Enrich the guild, project, and member data

  const idParts = version.subjectId.split(':');
  const parentId = idParts[0];
  let parentRef, parentData, parentName;
  if (parentId && parentCollection) {
    parentRef = `${parentCollection}/${parentId}`;
    parentData = refPathMap[parentRef];
    
    if (parentData) {
      if (parentCollection === 'guilds') {
        parentData = enrichGuild(parentData);
      } else if (parentCollection === 'projects') {
        parentData = enrichProject(parentData);
      }
      
      parentName = parentData.name;
    }
  }

  let proposer_member = version.proposer ? refPathMap[version.proposer] : null;
  if (proposer_member) proposer_member = enrichMember(proposer_member);

  let approvers_members = !version.approvers?.length ? [] : version.approvers
    .map((approverRef) => refPathMap[approverRef] ? enrichMember(refPathMap[approverRef]) : null);

  Object.assign(version, {
    isEnriched: true,

    detailViewPath: `/v/${version.id}`,

    parentCollection,
    parentRef,
    parentData,
    parentAvatarShape,
    parentName,

    proposer_member,
    approvers_members,

    subjectCollection_icon,
    subjectCollection_iconLibrary,
    subjectCollection_label,

    status_icon,
    status_iconLibrary,
    status_label,
    status_description,

    approvalStatus_icon,
    approvalStatus_iconLibrary,
    approvalStatus_label,
    approvalStatus_description,

    approvalFlow_icon,
    approvalFlow_iconLibrary,
    approvalFlow_label,
    approvalFlow_versionKey,

    proposerNote_firstParagraph: getFirstParagraph(
      version.proposerNote,
      FIRST_PARAGRAPH_MIN_LENGTH
    ),
    proposerNote_withQuotes: _addQuotes(version.proposerNote),

    contentData_label: (
      version.contentData?.role
        ? capFirstChar(version.contentData.role)
        : ''
    ),

    responses: version.responses?.map((response) => {
      const responseStatusSettings = versionResponseStatusMap[response.status];
      const status_icon = responseStatusSettings?.icon || 'help';
      const status_iconLibrary = responseStatusSettings?.iconLibrary || 'material';
      const status_label = responseStatusSettings?.label || 'Unknown';
      const status_description = responseStatusSettings?.description || '';

      return {
        ...response,

        status_icon,
        status_iconLibrary,
        status_label,
        status_description,

        memberRef: `members/${response.memberId}`,

        note_withQuotes: _addQuotes(response.note),
      };
    }),
  });

  return version;
};
